/**
 * Formatting + small data helpers for the Land Evaluation tool.
 *
 * Contract reminder (property-context 1.0.0): `null` means unknown, `0` is a
 * measured zero, `false` is a real "no". Formatters therefore return the
 * em-dash for null/undefined only and render zero as zero.
 */

export const DASH = '—';

export const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

const toNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function fmtNum(v, digits = 0) {
  const n = toNum(v);
  if (n === null) return DASH;
  return n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function fmtCompact(v, digits = 1) {
  const n = toNum(v);
  if (n === null) return DASH;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(digits).replace(/\.0+$/, '')}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(digits).replace(/\.0+$/, '')}M`;
  if (abs >= 1e4) return `${sign}${(abs / 1e3).toFixed(abs >= 1e5 ? 0 : digits).replace(/\.0+$/, '')}K`;
  return fmtNum(n, abs < 10 && !Number.isInteger(n) ? 1 : 0);
}

export function fmtMoney(v, { compact = false, digits = 0 } = {}) {
  const n = toNum(v);
  if (n === null) return DASH;
  if (compact) {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2).replace(/\.?0+$/, '')}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(abs >= 1e5 ? 0 : 1).replace(/\.0$/, '')}K`;
    return `${sign}$${abs.toFixed(0)}`;
  }
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Percent from a 0–100 number. */
export function fmtPct(v, digits = 1) {
  const n = toNum(v);
  if (n === null) return DASH;
  return `${n.toFixed(digits)}%`;
}

/** Percent from a 0–1 ratio. */
export function fmtRatioPct(v, digits = 0) {
  const n = toNum(v);
  if (n === null) return DASH;
  return `${(n * 100).toFixed(digits)}%`;
}

export function fmtSigned(v, digits = 1, suffix = '') {
  const n = toNum(v);
  if (n === null) return DASH;
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}${suffix}`;
}

export function fmtMultiple(v, digits = 2) {
  const n = toNum(v);
  if (n === null) return DASH;
  return `${n.toFixed(digits)}×`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Parse "YYYY-MM-DD" (or full ISO) without timezone drift for date-only strings. */
export function parseDate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(iso) {
  const d = parseDate(iso);
  if (!d) return DASH;
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

export function fmtMonth(iso) {
  const d = parseDate(iso);
  if (!d) return DASH;
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function fmtMonthShort(iso) {
  const d = parseDate(iso);
  if (!d) return DASH;
  return `${MONTHS[d.getUTCMonth()]} '${String(d.getUTCFullYear()).slice(2)}`;
}

export function fmtYear(iso) {
  const d = parseDate(iso);
  if (!d) return DASH;
  return String(d.getUTCFullYear());
}

export function fmtDateTime(iso) {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function fmtRelative(iso) {
  if (!iso) return DASH;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return DASH;
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  return fmtDate(iso);
}

export function fmtAcres(v) {
  const n = toNum(v);
  if (n === null) return DASH;
  return `${n.toFixed(n < 1 ? 2 : n < 10 ? 2 : 1)} ac`;
}

export function fmtSqft(v) {
  const n = toNum(v);
  if (n === null) return DASH;
  return `${fmtNum(n)} sq ft`;
}

export function fmtMiles(v) {
  const n = toNum(v);
  if (n === null) return DASH;
  return `${n.toFixed(n < 10 ? 1 : 0)} mi`;
}

export function fmtDuration(ms) {
  const n = toNum(ms);
  if (n === null) return DASH;
  if (n < 1000) return `${Math.round(n)} ms`;
  return `${(n / 1000).toFixed(1)}s`;
}

export function fmtBool(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return DASH;
}

/** "FOR_SALE" -> "For sale", "single_family" -> "Single family". */
export function titleCase(str) {
  if (str === null || str === undefined || str === '') return DASH;
  const s = String(str).replace(/[_-]+/g, ' ').trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function fmtCoords(lat, lng, digits = 5) {
  const a = toNum(lat);
  const b = toNum(lng);
  if (a === null || b === null) return DASH;
  return `${a.toFixed(digits)}, ${b.toFixed(digits)}`;
}

// ── Domain labels ───────────────────────────────────────────────────────────

export const SECTOR_LABELS = {
  agriculture: 'Agriculture',
  mining: 'Mining & extraction',
  utilities: 'Utilities',
  construction: 'Construction',
  manufacturing: 'Manufacturing',
  wholesale_trade: 'Wholesale trade',
  retail_trade: 'Retail trade',
  transportation_warehousing: 'Transportation & warehousing',
  transportation: 'Transportation & warehousing',
  information: 'Information',
  finance_insurance: 'Finance & insurance',
  real_estate: 'Real estate',
  professional_services: 'Professional services',
  management: 'Management of companies',
  administrative_support: 'Administrative support',
  admin_support: 'Administrative support',
  education: 'Education',
  healthcare: 'Health care',
  arts_entertainment: 'Arts & entertainment',
  accommodation_food: 'Accommodation & food',
  other_services: 'Other services',
  public_administration: 'Public administration',
};

export const EARNINGS_LABELS = {
  '1250_or_less_usd_per_month': '≤ $1,250 / mo',
  '1251_to_3333_usd_per_month': '$1,251–3,333 / mo',
  'over_3333_usd_per_month': '> $3,333 / mo',
};

export const WORKER_AGE_LABELS = {
  '29_or_younger': '29 or younger',
  '30_to_54': '30–54',
  '55_or_older': '55 or older',
};

export const PERMIT_LABELS = {
  one_unit: '1 unit',
  two_unit: '2 units',
  three_to_four_unit: '3–4 units',
  five_plus_unit: '5+ units',
};

export const DEED_TYPE_TONES = {
  resale: 'good',
  'new construction': 'good',
  reo: 'critical',
  'nominal/quit-claim': 'neutral',
  'nominal': 'neutral',
  'construction financing': 'warning',
  'pre-foreclosure': 'critical',
};

export function labelize(key, map) {
  if (key === null || key === undefined) return DASH;
  if (map && map[key]) return map[key];
  return titleCase(key);
}

/**
 * Named public places (property-context API 2.2.0 `nearby_places.public_places`).
 * Key order doubles as the display order; unknown keys fall back to title case.
 */
export const PUBLIC_PLACE_CATEGORY_LABELS = {
  park: 'Parks',
  playground: 'Playgrounds',
  dog_park: 'Dog parks',
  skate_park: 'Skate parks',
  trail: 'Trails',
  nature_preserve: 'Nature preserves',
  beach: 'Beaches',
  garden: 'Gardens',
  lake_waterfront: 'Lakes & waterfront',
  scenic_lookout: 'Scenic lookouts',
  swimming_pool: 'Swimming pools',
  sports_club: 'Sports clubs',
  recreation_center: 'Recreation centers',
  athletic_field: 'Athletic fields',
  golf_course: 'Golf courses',
  community_center: 'Community centers',
  library: 'Libraries',
  zoo_aquarium: 'Zoos & aquariums',
  amusement_water_park: 'Amusement & water parks',
  museum: 'Museums',
  stadium: 'Stadiums',
  tourist_attraction: 'Attractions',
};
export const PUBLIC_PLACE_CATEGORY_ORDER = Object.keys(PUBLIC_PLACE_CATEGORY_LABELS);

export function publicCategoryLabel(key) {
  return labelize(key, PUBLIC_PLACE_CATEGORY_LABELS);
}

export function transactionTone(type) {
  if (!type) return 'neutral';
  const t = String(type).toLowerCase();
  for (const key of Object.keys(DEED_TYPE_TONES)) {
    if (t.includes(key)) return DEED_TYPE_TONES[key];
  }
  return 'neutral';
}

// ── Data access helpers ─────────────────────────────────────────────────────

export const safeArray = (v) => (Array.isArray(v) ? v : []);

/** Latest year record from a BracketComponent ({unit, years: [{year, total, brackets}]}). */
export function latestYearRecord(component) {
  const years = safeArray(component?.years).filter((y) => y && isNum(y.year));
  if (years.length === 0) return null;
  return years.reduce((best, y) => (y.year > best.year ? y : best), years[0]);
}

/** Latest SeriesPoint of a series (sorted by date, nulls skipped). */
export function seriesLatest(points) {
  const pts = safeArray(points).filter((p) => p && p.date && isNum(p.value));
  if (pts.length === 0) return null;
  return pts.reduce((best, p) => (p.date > best.date ? p : best), pts[0]);
}

export function seriesSorted(points) {
  return safeArray(points)
    .filter((p) => p && p.date && isNum(p.value))
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** Value of the series closest to `yearsBack` years before its latest point. */
export function seriesPriorValue(points, yearsBack) {
  const sorted = seriesSorted(points);
  if (sorted.length < 2) return null;
  const latest = parseDate(sorted[sorted.length - 1].date);
  if (!latest) return null;
  const target = latest.getTime() - yearsBack * 365.25 * 86400000;
  let best = null;
  let bestDist = Infinity;
  for (const p of sorted) {
    const d = parseDate(p.date);
    if (!d) continue;
    const dist = Math.abs(d.getTime() - target);
    if (dist < bestDist) { bestDist = dist; best = p; }
  }
  if (!best || best === sorted[sorted.length - 1]) return null;
  return best;
}

export function pctChange(from, to) {
  const a = toNum(from);
  const b = toNum(to);
  if (a === null || b === null || a === 0) return null;
  return ((b - a) / Math.abs(a)) * 100;
}

/** Reference value from a SeriesPoint[] nearest to a given date string. */
export function seriesValueNear(points, dateIso) {
  const sorted = seriesSorted(points);
  const target = parseDate(dateIso);
  if (!target || sorted.length === 0) return null;
  let best = null;
  let bestDist = Infinity;
  for (const p of sorted) {
    const d = parseDate(p.date);
    if (!d) continue;
    const dist = Math.abs(d.getTime() - target.getTime());
    if (dist < bestDist) { bestDist = dist; best = p; }
  }
  // Only accept a reference within ~400 days of the target.
  return best && bestDist <= 400 * 86400000 ? best : null;
}

/** True when a record has at least one non-null, non-empty value. */
export function nonEmptyRecord(obj) {
  if (!obj || typeof obj !== 'object') return false;
  return Object.values(obj).some((v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0));
}

export function statusTone(status) {
  switch (status) {
    case 'ok': return 'good';
    case 'partial': return 'warning';
    case 'error': return 'critical';
    case 'not_requested': return 'muted';
    case 'excluded': return 'muted';
    default: return 'neutral'; // not_found, not_covered, not_available
  }
}

export const STATUS_LABELS = {
  ok: 'OK',
  partial: 'Partial',
  not_found: 'No record',
  not_covered: 'Not covered',
  not_available: 'Not available',
  error: 'Error',
  not_requested: 'Not requested',
  excluded: 'Excluded',
};

export function ratingTone(normalized) {
  const n = toNum(normalized);
  if (n === null) return 'na';
  if (n >= 3.3) return 'good';
  if (n >= 2.3) return 'mid';
  return 'low';
}

export function confidenceTone(level) {
  switch ((level || '').toLowerCase()) {
    case 'high': return 'good';
    case 'medium': return 'warning';
    case 'low': return 'critical';
    default: return 'neutral';
  }
}

/** Comparison against a national/reference value: {ratio, label, tone}. */
export function compareToReference(value, reference) {
  const v = toNum(value);
  const r = toNum(reference);
  if (v === null || r === null || r === 0) return null;
  const ratio = v / r;
  const diffPct = (ratio - 1) * 100;
  let tone = 'flat';
  if (diffPct >= 3) tone = 'up';
  else if (diffPct <= -3) tone = 'down';
  return { ratio, diffPct, label: `${diffPct >= 0 ? '+' : ''}${diffPct.toFixed(0)}% vs US`, tone };
}

/** Where a jobs/residents ratio sits on the bedroom ↔ employment-node spectrum. */
export function employmentCharacter(ratio) {
  const r = toNum(ratio);
  if (r === null) return { label: DASH, tone: 'neutral' };
  if (r >= 1.5) return { label: 'Employment node', tone: 'good' };
  if (r >= 1.0) return { label: 'Balanced, imports workers', tone: 'good' };
  if (r >= 0.6) return { label: 'Mostly residential', tone: 'neutral' };
  return { label: 'Bedroom community', tone: 'warning' };
}

/** Parse "lat, lng" (also "lat lng"; tolerant of parentheses). */
export function parseCoordinateText(text) {
  if (!text || typeof text !== 'string') return null;
  const cleaned = text.replace(/[()]/g, ' ').trim();
  const m = cleaned.match(/^(-?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  const latitude = Number(m[1]);
  const longitude = Number(m[2]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude, longitude };
}

/**
 * Build a clean "street, city, ST zip" line from a Mapbox geocoding feature.
 * The backend passes the raw address to vendor lookups, so we avoid the
 * spelled-out state and the ", United States" suffix Mapbox appends.
 */
export function formatMapboxFeatureAddress(feature) {
  if (!feature) return '';
  const ctx = safeArray(feature.context);
  const find = (prefix) => ctx.find((c) => typeof c?.id === 'string' && c.id.startsWith(`${prefix}.`));
  const place = find('place') || find('locality');
  const region = find('region');
  const postcode = find('postcode');
  const stateCode = region?.short_code ? region.short_code.replace(/^US-/i, '').toUpperCase() : null;
  const isAddress = Array.isArray(feature.place_type) && feature.place_type.includes('address');
  const street = isAddress
    ? [feature.address, feature.text].filter(Boolean).join(' ')
    : feature.text;
  const parts = [street, place?.text, [stateCode, postcode?.text].filter(Boolean).join(' ')].filter(Boolean);
  if (parts.length >= 2) return parts.join(', ');
  return (feature.place_name || '').replace(/,\s*United States$/i, '');
}

/** Nice axis ticks (1-2-5 progression). */
export function niceTicks(min, max, count = 5) {
  if (!isNum(min) || !isNum(max)) return [];
  if (min === max) {
    if (min === 0) return [0, 1];
    const pad = Math.abs(min) * 0.1;
    min -= pad; max += pad;
  }
  const span = max - min;
  const rough = span / Math.max(1, count);
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / mag;
  let step;
  if (norm <= 1) step = 1;
  else if (norm <= 2) step = 2;
  else if (norm <= 2.5) step = 2.5;
  else if (norm <= 5) step = 5;
  else step = 10;
  step *= mag;
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = start; v <= end + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
  return ticks;
}

/** Canonical addresses arrive with a country suffix that is noise in a US-only tool. */
export function displayAddress(formatted) {
  if (!formatted || typeof formatted !== 'string') return formatted || '';
  return formatted.replace(/,\s*United States( of America)?$/i, '').trim();
}
